import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth-compat';
import {
  getAvailability,
  replaceAvailability,
  getLearningPreferences,
  updateLearningPreferences,
  listSecondarySubjects,
  addSecondarySubject,
  removeSecondarySubject,
  type AddSecondarySubjectInput,
} from '@/services/enrichmentService';
import type { AvailabilitySlot, LearningPreferences, LearningStyle } from '@/types/registeredStudent';

const AVAILABILITY_KEY = ['availability'] as const;
const PREFS_KEY = ['learningPreferences'] as const;
const SECONDARY_KEY = ['secondarySubjects'] as const;

/** Real API-backed enrichment (availability, learning preferences, secondary subjects). */
export function useEnrichment() {
  const { user } = useAuth();
  const token = user?.access_token ?? '';
  const qc = useQueryClient();

  // ── Availability ──────────────────────────────────────────────────────────
  const availQuery = useQuery<AvailabilitySlot[]>({
    queryKey: AVAILABILITY_KEY,
    enabled: !!token,
    queryFn: () => getAvailability(token),
  });

  const replaceAvailMutation = useMutation({
    mutationFn: (slots: AvailabilitySlot[]) => replaceAvailability(token, slots),
    onSuccess: () => qc.invalidateQueries({ queryKey: AVAILABILITY_KEY }),
  });

  // ── Learning preferences ──────────────────────────────────────────────────
  const prefsQuery = useQuery<LearningPreferences>({
    queryKey: PREFS_KEY,
    enabled: !!token,
    queryFn: () => getLearningPreferences(token),
  });

  const updatePrefsMutation = useMutation({
    mutationFn: (prefs: LearningPreferences) => updateLearningPreferences(token, prefs),
    onSuccess: () => qc.invalidateQueries({ queryKey: PREFS_KEY }),
  });

  // ── Secondary subjects ────────────────────────────────────────────────────
  const secondaryQuery = useQuery({
    queryKey: SECONDARY_KEY,
    enabled: !!token,
    queryFn: () => listSecondarySubjects(token),
  });

  const addSecondaryMutation = useMutation({
    mutationFn: (input: AddSecondarySubjectInput) => addSecondarySubject(token, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SECONDARY_KEY }),
  });

  const removeSecondaryMutation = useMutation({
    mutationFn: (id: string) => removeSecondarySubject(token, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: SECONDARY_KEY }),
  });

  // ── Public API ────────────────────────────────────────────────────────────
  const saveAvailability = (slots: AvailabilitySlot[]) =>
    replaceAvailMutation.mutateAsync(slots);

  const saveLearningStyle = (learningStyle: LearningStyle | null) =>
    updatePrefsMutation.mutateAsync({ learningStyle });

  const addSubject = (input: Omit<AddSecondarySubjectInput, never>) =>
    addSecondaryMutation.mutateAsync(input);

  const removeSubject = (id: string) => removeSecondaryMutation.mutateAsync(id);

  return {
    availability: availQuery.data ?? [],
    prefs: prefsQuery.data ?? {},
    secondary: secondaryQuery.data ?? [],
    saveAvailability,
    saveLearningStyle,
    addSubject,
    removeSubject,
    isLoading:
      availQuery.isLoading || prefsQuery.isLoading || secondaryQuery.isLoading,
  };
}
